import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from "react-intl";
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
class JsonBroadcastChannel extends BroadcastChannel {
    constructor(name, senderID = generateId()) {
        super(name);
        this._events = {};
        this.onmessage = this.handleMessage;
        this.senderId = senderID;
    }
    handleMessage(ev) {
        try {
            const data = JSON.parse(ev.data);
            const type = data["$type"];
            const sender = data["$sender"];
            this._events[type]?.(sender, data);
        }
        catch (_) { }
    }
    on(eventType, handler) {
        this._events[eventType] = handler;
    }
    off(eventType) {
        delete this._events[eventType];
    }
    postJsonMessage(type, data) {
        const payload = { ...data, $type: type, $sender: this.senderId };
        const stringified = JSON.stringify(payload);
        super.postMessage(stringified);
    }
}
class SingleInstanceChannel extends JsonBroadcastChannel {
    constructor(name, senderID = generateId()) {
        super(`single-instance:${name}`, senderID);
    }
}
const generateId = () => Math.random().toString(16).slice(2);
export const messages = defineMessages({
    title: {
        id: "singleInstance.title",
        defaultMessage: "Multiple instances detected",
    },
    body: {
        id: "singleInstance.body",
        defaultMessage: "Another instance of this application is already open. Please use only one instance. This popup will close when all other instances are closed.",
    },
    ignoreButton: {
        id: "singleInstance.ignoreButton",
        defaultMessage: "Ignore",
    },
    confirm: {
        id: "singleInstance.confirm",
        defaultMessage: "Are you sure you want to ignore this warning? This may lead to data conflicts.",
    }
});
export function SingleInstance({ id = "default", debug = false }) {
    const winId = useRef(generateId());
    const bcRef = useRef(new SingleInstanceChannel(id));
    const bc = bcRef.current;
    const [openWindows, setOpenWindows] = useState([winId.current]);
    const [ignored, setIgnored] = useState(false);
    const intl = useIntl();
    useEffect(() => {
        if (debug)
            console.debug("Announcing creation of single-instance window: %s", winId.current);
        bc.postJsonMessage("announce", { name: winId.current });
        const closeHandler = () => bc.postJsonMessage("closeWindow", { name: winId.current });
        window.addEventListener("beforeunload", closeHandler);
        return () => {
            window.removeEventListener("beforeunload", closeHandler);
            bc.close();
        };
    }, []);
    useEffect(() => {
        bc.on("announce", (sender) => {
            if (debug)
                console.debug("Received 'announce' from: %s", sender);
            if (sender === winId.current || openWindows.includes(sender))
                return;
            setOpenWindows([...openWindows, sender]);
            bc.postJsonMessage("acknowledge", { target: sender });
        });
        bc.on("acknowledge", (sender, data) => {
            if (debug)
                console.debug("Received 'acknowledge' from: %s", sender);
            if (data.target === winId.current || openWindows.includes(sender))
                return;
            setOpenWindows([...openWindows, sender]);
        });
        bc.on("closeWindow", (sender) => {
            if (debug)
                console.debug("Recevied 'closeWindow' from: %s", sender);
            setOpenWindows(openWindows.filter((id) => id !== sender));
        });
    }, [openWindows]);
    const handleIgnore = () => {
        setIgnored(window.confirm(intl.formatMessage(messages.confirm)));
    };
    return (_jsx(Modal, { open: openWindows.length > 1 && !ignored, children: _jsxs("div", { style: {
                backgroundColor: "white",
                padding: "20px",
                margin: "100px auto",
                maxWidth: "400px",
                outline: "none"
            }, children: [_jsx("h2", { children: intl.formatMessage(messages.title) }), _jsx("p", { children: intl.formatMessage(messages.body) }), _jsx(Button, { variant: "contained", color: "secondary", onClick: handleIgnore, children: intl.formatMessage(messages.ignoreButton) })] }) }));
}
//# sourceMappingURL=SingleInstance.js.map